import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { HandHeart, Plus, Eye, Trash2, RefreshCw, User, FileText, BarChart3, FileSpreadsheet, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime as fmtDateTimeTz } from '../lib/datetime';
import { useArticleUnits } from '../hooks/useArticleUnits';
import { useFormDraft } from '../hooks/useFormDraft';
import { DraftBanner } from '../components/DraftBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const perms = user.permissions || {};
  return perms?.[module]?.[action] === true;
};

const fmtDateTime = (d) => fmtDateTimeTz(d, { hour: '2-digit', minute: '2-digit' });

const EMPTY_ITEM = { article_id: '', article_name: '', lot_number: '', expiration_date: '', quantity: 1, notes: '' };

function NursingReceptions() {
  const { user } = useOutletContext();
  const { unitOf } = useArticleUnits();
  // v3.45.1 · Permiso delegable para dar de alta artículos nuevos al cargar REM.
  const canCreateNewArticle = userCan(user, 'recepciones_internas', 'crear_articulo_nuevo');
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [areas, setAreas] = useState([]);
  const [articles, setArticles] = useState([]);
  const [filters, setFilters] = useState({ area_id: '', patient_name: '', dni: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportFilters, setReportFilters] = useState({ start_date: '', end_date: '', area_id: '' });
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [form, setForm] = useState({
    area_id: '',
    patient_name: '', patient_floor: '', patient_bed: '',
    responsible_name: '', responsible_dni: '', responsible_relationship: '',
    notes: '',
    items: [{ ...EMPTY_ITEM }],
  });

  const canCreate = userCan(user, 'recepciones_internas', 'crear');
  const canDelete = userCan(user, 'recepciones_internas', 'eliminar');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { drafts, loadDrafts, getDraft, saveDraft, deleteDraft } = useFormDraft('rem');
  const [draftSaving, setDraftSaving] = useState(false);
  const [pendingNotice, setPendingNotice] = useState(null); // v3.45.0 · modal post-REM
  const [draftInfo, setDraftInfo] = useState(null);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const savingRef = useRef(false);
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; });

  const applyDraft = (d) => {
    const p = d.payload || {};
    setForm({
      area_id: d.area_id,
      patient_name: p.patient_name || '', patient_floor: p.patient_floor || '', patient_bed: p.patient_bed || '',
      responsible_name: p.responsible_name || '', responsible_dni: p.responsible_dni || '',
      responsible_relationship: p.responsible_relationship || '',
      notes: p.notes || '',
      items: p.items?.length ? p.items : [{ ...EMPTY_ITEM }],
    });
    setDraftInfo(d);
    setCreateOpen(true);
    toast.info(`Retomando el borrador guardado por ${d.updated_by_name}`);
  };

  const onAreaChange = async (areaId) => {
    setForm(f => ({ ...f, area_id: areaId }));
    setDraftInfo(null);
    if (!areaId) return;
    const d = await getDraft(areaId);
    if (!d?.payload) return;
    // Bajo latencia, el usuario puede haber empezado a tipear antes de que llegue
    // el borrador. Solo lo aplicamos si el formulario sigue vacío; si no, avisamos
    // y que lo retome desde el banner, sin pisar lo cargado.
    const cur = formRef.current || {};
    // Si el usuario ya cambió de área mientras llegaba este borrador, lo ignoramos.
    if (cur.area_id !== areaId) return;
    const pristine = !cur.patient_name && !cur.patient_floor && !cur.patient_bed &&
      !cur.responsible_name && !cur.responsible_dni && !cur.responsible_relationship && !cur.notes &&
      (cur.items || []).every(it => !it.article_id && !it.article_name && !it.lot_number && !it.expiration_date && !it.notes);
    if (pristine) {
      applyDraft(d);
    } else {
      toast.info('Hay un borrador guardado para esta área. Usá "Continuar" en el banner para retomarlo sin perder lo que ya cargaste.');
    }
  };

  const doSaveDraft = async (silent = false) => {
    if (!form.area_id) {
      if (!silent) toast.error('Elegí primero el área para guardar el borrador');
      return;
    }
    // El autoguardado silencioso no debe interferir con la carga: no toca el
    // estado del botón ni el banner superior, y evita solaparse consigo mismo.
    if (silent && savingRef.current) return;
    savingRef.current = true;
    if (!silent) setDraftSaving(true);
    const { area_id, ...payload } = form;
    const d = await saveDraft(area_id, payload, { silent });
    savingRef.current = false;
    if (!silent) setDraftSaving(false);
    if (d) {
      if (silent) {
        setAutoSavedAt(d.updated_at || new Date().toISOString());
      } else {
        setDraftInfo(d);
        toast.success('Borrador guardado: podés cerrar y seguir después');
      }
    } else if (!silent) {
      toast.error('No se pudo guardar el borrador');
    }
  };

  const discardDraft = async (d) => {
    await deleteDraft(d.area_id);
    if (form.area_id === d.area_id) setDraftInfo(null);
    toast.success('Borrador descartado');
  };

  // Referencia al último doSaveDraft para que el intervalo NO se recree en cada
  // tecla (antes dependía de `form` entero). Así el autoguardado tiene cadencia
  // estable de 30 s y siempre guarda el estado más reciente del formulario.
  const doSaveDraftRef = useRef(doSaveDraft);
  doSaveDraftRef.current = doSaveDraft;

  // Autoguardado cada 30 s mientras se carga (por si interrumpen al usuario).
  useEffect(() => {
    if (!createOpen || !form.area_id) return;
    const t = setInterval(() => { doSaveDraftRef.current(true); }, 30000);
    return () => clearInterval(t);
  }, [createOpen, form.area_id]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await axios.delete(`${BACKEND_URL}/api/nursing-receptions/${deleteTarget.reception_id}`, { withCredentials: true });
      toast.success(`REM ${r.data.reception_number} eliminada — se revirtió el stock (${r.data.reverted_lots} lote/s)`);
      setDeleteTarget(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar la REM');
    } finally { setDeleting(false); }
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.area_id) params.append('area_id', filters.area_id);
      if (filters.patient_name) params.append('patient_name', filters.patient_name);
      if (filters.dni) params.append('dni', filters.dni);
      const r = await axios.get(`${BACKEND_URL}/api/nursing-receptions?${params.toString()}`, { withCredentials: true });
      setList(r.data || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error('No tenés permiso para ver recepciones externas.');
      else toast.error('Error al cargar recepciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchMasters = async () => {
    // Áreas donde el usuario puede INGRESAR una REM (las suyas + las habilitadas por un admin).
    axios.get(`${BACKEND_URL}/api/nursing-receptions/rem-areas`, { withCredentials: true })
      .then(r => setAreas(r.data || []))
      .catch(() => setAreas([]));
    axios.get(`${BACKEND_URL}/api/articles`, { withCredentials: true })
      .then(r => setArticles((r.data || []).filter(a => a.is_medicamento)))
      .catch(() => setArticles([]));
  };

  useEffect(() => {
    (async () => {
      await fetchMasters();
      await fetchList();
      if (userCan(user, 'recepciones_internas', 'crear')) loadDrafts();
    })();
  }, []);

  const totalItems = useMemo(
    () => list.reduce((acc, r) => acc + (r.items?.length || 0), 0),
    [list]
  );

  const resetForm = () => { setDraftInfo(null); setAutoSavedAt(null); setForm({
    area_id: '', patient_name: '', patient_floor: '', patient_bed: '',
    responsible_name: '', responsible_dni: '', responsible_relationship: '',
    notes: '', items: [{ ...EMPTY_ITEM }],
  }); };

  // Actualizaciones FUNCIONALES: imprescindibles porque selectFromCombobox /
  // selectFromBarrier / confirmCreateFromBarrier están memoizados y, con la forma
  // setForm({...form}), capturaban un `form` obsoleto (del primer render) que
  // borraba área/paciente/responsable al elegir un artículo. Con setForm(f=>...)
  // siempre se parte del estado más reciente.
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, patch) => setForm(f => ({
    ...f,
    items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it)
  }));

  const [suggest, setSuggest] = useState({});   // índice de ítem -> sugerencias del catálogo (LEGACY — reemplazado por artQuery/createBarrier)

  // v3.45.2 — Combobox de búsqueda en tiempo real + barrera de similitud
  const [artQuery, setArtQuery] = useState({});   // { [i]: string } texto escrito en el combobox
  const [artOpen, setArtOpen] = useState({});     // { [i]: bool } dropdown visible
  const [createBarrier, setCreateBarrier] = useState(null);
  // createBarrier: { itemIdx, pendingName, similar, barrierConfirmed }

  const artFilteredFor = useCallback((i) => {
    const q = (artQuery[i] || '').trim().toLowerCase();
    if (q.length < 2) return [];
    return articles
      .filter(a => a.name.toLowerCase().includes(q))
      .slice(0, 10);
  }, [artQuery, articles]);

  const selectFromCombobox = useCallback((i, article) => {
    updateItem(i, { article_id: article.article_id, article_name: article.name });
    setArtQuery(q => ({ ...q, [i]: article.name }));
    setArtOpen(o => ({ ...o, [i]: false }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreateBarrier = useCallback(async (i, name) => {
    setArtOpen(o => ({ ...o, [i]: false }));
    try {
      const r = await axios.get(`${BACKEND_URL}/api/nursing-receptions/article-suggestions`,
        { params: { name }, withCredentials: true });
      setCreateBarrier({
        itemIdx: i,
        pendingName: name,
        similar: r.data?.similar || [],
        exact: r.data?.exact || null,
        barrierConfirmed: false,
      });
    } catch {
      setCreateBarrier({ itemIdx: i, pendingName: name, similar: [], exact: null, barrierConfirmed: false });
    }
  }, []);

  const confirmCreateFromBarrier = useCallback(() => {
    if (!createBarrier) return;
    const { itemIdx, pendingName } = createBarrier;
    updateItem(itemIdx, { article_id: '', article_name: pendingName });
    setArtQuery(q => ({ ...q, [itemIdx]: pendingName }));
    setCreateBarrier(null);
  }, [createBarrier]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectFromBarrier = useCallback((article) => {
    if (!createBarrier) return;
    const i = createBarrier.itemIdx;
    updateItem(i, { article_id: article.article_id, article_name: article.name });
    setArtQuery(q => ({ ...q, [i]: article.name }));
    setCreateBarrier(null);
    toast.success(`Seleccionado: ${article.name}`);
  }, [createBarrier]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkArticleName = async (i, name) => {
    if (!name || name.trim().length < 3) { setSuggest(s => ({ ...s, [i]: null })); return; }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/nursing-receptions/article-suggestions`,
        { params: { name }, withCredentials: true });
      setSuggest(s => ({ ...s, [i]: r.data }));
    } catch { setSuggest(s => ({ ...s, [i]: null })); }
  };

  const onSelectArticle = (i, val) => {
    setSuggest(s => ({ ...s, [i]: null }));
    if (val === '__new__') {
      updateItem(i, { article_id: '', article_name: '' });
      return;
    }
    const a = articles.find(x => x.article_id === val);
    if (a) updateItem(i, { article_id: a.article_id, article_name: a.name });
    else updateItem(i, { article_id: '', article_name: '' });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.area_id) return toast.error('Seleccioná el área');
    if (!form.patient_name.trim()) return toast.error('Falta el nombre del residente');
    if (!form.responsible_name.trim() || !form.responsible_dni.trim()) {
      return toast.error('Faltan datos del responsable (nombre y DNI)');
    }
    if (form.items.length === 0) return toast.error('Agregá al menos un ítem');
    for (const it of form.items) {
      if (!it.article_name.trim()) return toast.error('Hay ítems sin nombre de medicamento');
      if (!canCreateNewArticle && !it.article_id) {
        return toast.error(`«${it.article_name.trim()}» no está en el catálogo. No tenés permiso para dar de alta medicamentos nuevos — elegilo del catálogo o pedí a Farmacia que lo homologue.`);
      }
      if (!it.lot_number.trim()) return toast.error('Cada ítem necesita número de lote');
      if (!it.quantity || it.quantity < 1) return toast.error('La cantidad debe ser ≥ 1');
    }

    try {
      const payload = {
        ...form,
        items: form.items.map(it => ({
          article_id: it.article_id || null,
          article_name: it.article_name.trim(),
          lot_number: it.lot_number.trim(),
          expiration_date: it.expiration_date || null,
          quantity: Number(it.quantity),
          notes: it.notes?.trim() || null,
        })),
      };
      const r = await axios.post(`${BACKEND_URL}/api/nursing-receptions`, payload, { withCredentials: true });
      toast.success(`Recepción ${r.data.reception_number} creada`, {
        action: { label: 'Imprimir', onClick: () => handlePrint(r.data) },
        duration: 8000,
      });
      await deleteDraft(form.area_id);
      setCreateOpen(false);
      resetForm();
      fetchList();
      fetchMasters(); // recargar catálogo por si se crearon artículos nuevos
      // v3.45.0 · Si la REM dejó ítems en standby de homologación, mostrar
      // modal de aviso al enfermero para que avise a Farmacia.
      const pendings = r.data.pending_homologation_items || [];
      if (pendings.length > 0) {
        setPendingNotice({
          reception_number: r.data.reception_number,
          items: pendings,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear recepción');
    }
  };

  const openDetail = async (rec) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/nursing-receptions/${rec.reception_id}`, { withCredentials: true });
      setDetail(r.data);
    } catch (e) {
      toast.error('Error al cargar detalle');
    }
  };

  const handlePrint = (rec) => {
    // Abrir el PDF en una pestaña nueva. Como el endpoint requiere sesión con cookies,
    // axios no nos sirve para descargar; abrimos directo en una window.
    const url = `${BACKEND_URL}/api/nursing-receptions/${rec.reception_id}/pdf`;
    window.open(url, '_blank', 'noopener');
  };

  const reportQuery = () => {
    const p = new URLSearchParams();
    if (reportFilters.start_date) p.append('start_date', reportFilters.start_date);
    if (reportFilters.end_date) p.append('end_date', reportFilters.end_date);
    if (reportFilters.area_id) p.append('area_id', reportFilters.area_id);
    return p.toString();
  };

  const loadReport = async () => {
    try {
      setReportLoading(true);
      const r = await axios.get(`${BACKEND_URL}/api/nursing-receptions/report?${reportQuery()}`, { withCredentials: true });
      setReportData(r.data);
    } catch (e) {
      toast.error('No se pudo generar el reporte');
    } finally {
      setReportLoading(false);
    }
  };

  const openReport = () => {
    setReportOpen(true);
    setReportData(null);
    setReportFilters({ start_date: '', end_date: '', area_id: '' });
  };

  const downloadReportPdf = () => {
    window.open(`${BACKEND_URL}/api/nursing-receptions/report/pdf?${reportQuery()}`, '_blank', 'noopener');
  };
  const downloadReportExcel = () => {
    window.open(`${BACKEND_URL}/api/nursing-receptions/report/excel?${reportQuery()}`, '_blank', 'noopener');
  };

  return (
    <div className="space-y-6" data-testid="nursing-receptions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <HandHeart className="w-7 h-7 text-rose-600" /> Recepciones Externas (REM)
          </h1>
          <p className="text-muted-foreground mt-1">
            Medicación traída por familiares para uso exclusivo de un residente del Hogar.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchList}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
          </Button>
          <Button variant="outline" onClick={openReport} data-testid="rem-report-button">
            <BarChart3 className="w-4 h-4 mr-2" /> Reporte mensual
          </Button>
          {canCreate && (
            <Button onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="new-rem-button">
              <Plus className="w-4 h-4 mr-2" /> Nueva recepción
            </Button>
          )}
        </div>
      </div>

      {canCreate && (
        <DraftBanner
          drafts={drafts}
          label="Borrador de REM en curso"
          onContinue={applyDraft}
          onDiscard={discardDraft}
          testid="rem-draft-banner"
        />
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Recepciones</CardDescription></CardHeader>
          <CardContent><p className="text-3xl font-bold">{list.length}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Ítems totales</CardDescription></CardHeader>
          <CardContent><p className="text-3xl font-bold">{totalItems}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Residentes con medicación externa</CardDescription></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{new Set(list.map(r => r.patient_name)).size}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Listado</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label>Área</Label>
              <select
                value={filters.area_id}
                onChange={e => setFilters({ ...filters, area_id: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">(Todas)</option>
                {areas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Residente</Label>
              <Input value={filters.patient_name} onChange={e => setFilters({ ...filters, patient_name: e.target.value })} placeholder="Nombre y apellido…" />
            </div>
            <div>
              <Label>DNI Responsable</Label>
              <Input value={filters.dni} onChange={e => setFilters({ ...filters, dni: e.target.value })} placeholder="Ej: 12345678" />
            </div>
            <div className="flex items-end">
              <Button variant="outline" className="w-full" onClick={fetchList}>Aplicar filtros</Button>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-muted-foreground">Cargando…</div>
          ) : list.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground" data-testid="empty-state">
              No hay recepciones registradas todavía.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº</TableHead>
                  <TableHead>Fecha / Hora</TableHead>
                  <TableHead>Área</TableHead>
                  <TableHead>Residente</TableHead>
                  <TableHead>Responsable (DNI)</TableHead>
                  <TableHead>Recibió</TableHead>
                  <TableHead className="text-right">Ítems</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map(r => (
                  <TableRow key={r.reception_id} data-testid={`rem-row-${r.reception_id}`}>
                    <TableCell className="font-medium">{r.reception_number}</TableCell>
                    <TableCell>{fmtDateTime(r.received_at || r.created_at)}</TableCell>
                    <TableCell>{r.area_name}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{r.patient_name}</span>
                        {(r.patient_floor || r.patient_bed || r.patient_room) && (
                          <span className="text-xs text-muted-foreground">
                            {r.patient_floor && `Piso: ${r.patient_floor}`}
                            {r.patient_floor && r.patient_bed && ' · '}
                            {r.patient_bed && `Habitación: ${r.patient_bed}`}
                            {!r.patient_floor && !r.patient_bed && r.patient_room && `Sala/Habitación: ${r.patient_room}`}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{r.responsible_name}</span>
                        <span className="text-xs text-muted-foreground">DNI {r.responsible_dni}{r.responsible_relationship ? ` · ${r.responsible_relationship}` : ''}</span>
                      </div>
                    </TableCell>
                    <TableCell>{r.received_by_name}</TableCell>
                    <TableCell className="text-right">{r.items?.length || 0}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handlePrint(r)} title="Imprimir formulario PDF" data-testid={`print-${r.reception_id}`}>
                          <FileText className="w-4 h-4 text-indigo-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openDetail(r)} data-testid={`detail-${r.reception_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} title="Eliminar REM (revierte stock)" data-testid={`delete-${r.reception_id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rem-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HandHeart className="w-5 h-5 text-rose-600" /> Nueva Recepción Externa de Medicación</DialogTitle>
            <DialogDescription>Registrá el ingreso de medicación traída por un familiar para un residente específico.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {draftInfo && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="rem-draft-note">
                Borrador guardado por {draftInfo.updated_by_name} · {fmtDateTime(draftInfo.updated_at)}. Se guarda solo cada 30 segundos; el stock ingresa recién al <b>Registrar Recepción</b>.
              </p>
            )}
            {/* Área */}
            <div>
              <Label>Área (subdepósito destino) *</Label>
              <select
                value={form.area_id}
                onChange={e => onAreaChange(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                required
                data-testid="rem-area"
              >
                <option value="">(Seleccionar)</option>
                {areas.map(a => (
                  <option key={a.area_id} value={a.area_id}>{a.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Se listan las áreas donde podés ingresar REM: las tuyas y las que un administrador te habilitó como destino (ej. VERDE / VIOLETA).</p>
            </div>

            {/* Residente */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 border-t pt-3">
              <div className="sm:col-span-2">
                <Label>Residente *</Label>
                <Input value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} placeholder="Nombre y Apellido" required data-testid="rem-patient" />
              </div>
              <div>
                <Label>Piso</Label>
                <Input value={form.patient_floor} onChange={e => setForm({ ...form, patient_floor: e.target.value })} placeholder="Ej: PA" data-testid="rem-floor" />
              </div>
              <div>
                <Label>Habitación</Label>
                <Input value={form.patient_bed} onChange={e => setForm({ ...form, patient_bed: e.target.value })} placeholder="Ej: 12" data-testid="rem-bed" />
              </div>
            </div>

            {/* Responsable */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t pt-3">
              <div>
                <Label>Responsable (quien trae) *</Label>
                <Input value={form.responsible_name} onChange={e => setForm({ ...form, responsible_name: e.target.value })} placeholder="Nombre y Apellido" required data-testid="rem-resp-name" />
              </div>
              <div>
                <Label>DNI *</Label>
                <Input value={form.responsible_dni} onChange={e => setForm({ ...form, responsible_dni: e.target.value })} placeholder="Ej: 23456789" required data-testid="rem-resp-dni" />
              </div>
              <div>
                <Label>Vínculo</Label>
                <select
                  value={form.responsible_relationship}
                  onChange={e => setForm({ ...form, responsible_relationship: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">(Sin especificar)</option>
                  <option value="Hijo/a">Hijo/a</option>
                  <option value="Cónyuge">Cónyuge</option>
                  <option value="Padre/Madre">Padre/Madre</option>
                  <option value="Hermano/a">Hermano/a</option>
                  <option value="Otro familiar">Otro familiar</option>
                  <option value="Apoderado">Apoderado</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
            </div>

            {/* Items */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Medicación recibida *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="rem-add-item">
                  <Plus className="w-4 h-4 mr-1" /> Agregar ítem
                </Button>
              </div>
              <div className="space-y-3">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2 bg-muted/30">
                    <div className="col-span-12 sm:col-span-4">
                      <Label className="text-xs">Medicamento</Label>

                      {/* v3.45.2 — Combobox con búsqueda en tiempo real */}
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                        <Input
                          className="pl-7"
                          placeholder="Buscar medicamento…"
                          value={artQuery[i] ?? it.article_name}
                          data-testid={`rem-article-search-${i}`}
                          autoComplete="off"
                          onChange={e => {
                            const v = e.target.value;
                            setArtQuery(q => ({ ...q, [i]: v }));
                            setArtOpen(o => ({ ...o, [i]: true }));
                            if (!v) updateItem(i, { article_id: '', article_name: '' });
                          }}
                          onFocus={() => setArtOpen(o => ({ ...o, [i]: true }))}
                          onBlur={() => setTimeout(() => setArtOpen(o => ({ ...o, [i]: false })), 180)}
                        />

                        {/* Dropdown de resultados */}
                        {artOpen[i] && (artQuery[i] || '').trim().length >= 2 && (
                          <div className="absolute z-30 mt-1 w-full border rounded-md bg-background shadow-lg max-h-56 overflow-y-auto">
                            {artFilteredFor(i).length === 0 && (
                              <div className="px-3 py-2 text-xs text-muted-foreground italic">
                                Sin coincidencias en el catálogo
                              </div>
                            )}
                            {artFilteredFor(i).map(a => (
                              <button
                                key={a.article_id}
                                type="button"
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center justify-between gap-2"
                                data-testid={`rem-article-option-${i}-${a.article_id}`}
                                onMouseDown={() => selectFromCombobox(i, a)}
                              >
                                <span>{a.name}</span>
                                <span className="text-xs text-muted-foreground shrink-0">{a.unit || 'unidad'}</span>
                              </button>
                            ))}

                            {/* Solicitar alta — solo si tiene permiso y escribió al menos 3 caracteres */}
                            {canCreateNewArticle && (artQuery[i] || '').trim().length >= 3 && (
                              <button
                                type="button"
                                className="w-full text-left px-3 py-2 text-xs border-t border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center gap-2"
                                data-testid={`rem-create-new-${i}`}
                                onMouseDown={() => openCreateBarrier(i, (artQuery[i] || '').trim())}
                              >
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                No está en el catálogo — Solicitar alta de «{(artQuery[i] || '').trim()}»
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Indicador de artículo seleccionado */}
                      {it.article_id && (
                        <p className="mt-1 text-[11px] text-emerald-700 flex items-center gap-1" data-testid={`rem-article-confirmed-${i}`}>
                          ✔ Del catálogo
                        </p>
                      )}
                      {!it.article_id && it.article_name && (
                        <p className="mt-1 text-[11px] text-amber-700 flex items-center gap-1" data-testid={`rem-article-new-${i}`}>
                          <AlertTriangle className="w-3 h-3" /> Alta pendiente de homologación
                        </p>
                      )}
                      {!canCreateNewArticle && (
                        <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`rem-no-create-hint-${i}`}>
                          Si no está en el catálogo, pedí a Farmacia que lo homologue antes.
                        </p>
                      )}
                    </div>
                    <div className="col-span-6 sm:col-span-2">
                      <Label className="text-xs">Lote</Label>
                      <Input value={it.lot_number} onChange={e => updateItem(i, { lot_number: e.target.value })} placeholder="Lote" />
                    </div>
                    <div className="col-span-6 sm:col-span-2">
                      <Label className="text-xs">Vencimiento</Label>
                      <Input type="date" min={new Date().toISOString().slice(0,10)} value={it.expiration_date} onChange={e => updateItem(i, { expiration_date: e.target.value })} />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <Label className="text-xs">Cantidad {it.article_id ? <span className="text-muted-foreground">({unitOf(it.article_id)})</span> : null}</Label>
                      <Input type="number" min="1" className="text-right" value={it.quantity} onChange={e => updateItem(i, { quantity: e.target.value })} />
                    </div>
                    <div className="col-span-7 sm:col-span-1">
                      <Label className="text-xs">Obs.</Label>
                      <Input value={it.notes} onChange={e => updateItem(i, { notes: e.target.value })} placeholder="Opc." />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {form.items.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)}>
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notas */}
            <div className="border-t pt-3">
              <Label>Observaciones generales</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notas adicionales (estado del envase, indicaciones, etc.)" />
            </div>

            <DialogFooter>
              {autoSavedAt && (
                <span className="mr-auto self-center text-xs text-muted-foreground" data-testid="rem-autosave-indicator">
                  Autoguardado · {fmtDateTime(autoSavedAt)}
                </span>
              )}
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => doSaveDraft(false)}
                disabled={!form.area_id || draftSaving}
                data-testid="rem-save-draft"
              >
                {draftSaving ? 'Guardando…' : 'Guardar borrador'}
              </Button>
              <Button type="submit" data-testid="rem-submit">Registrar Recepción</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rem-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> {detail?.reception_number}</DialogTitle>
            <DialogDescription>
              {fmtDateTime(detail?.received_at)} · Recibió: {detail?.received_by_name}
            </DialogDescription>
          </DialogHeader>

          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><strong>Área:</strong> {detail.area_name}</div>
                <div><strong>Residente:</strong> {detail.patient_name}{(detail.patient_floor || detail.patient_bed) ? ` — Piso ${detail.patient_floor || '-'} · Cama ${detail.patient_bed || '-'}` : (detail.patient_room ? ` (${detail.patient_room})` : '')}</div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span><strong>Responsable:</strong> {detail.responsible_name} — DNI {detail.responsible_dni}{detail.responsible_relationship ? ` · ${detail.responsible_relationship}` : ''}</span>
                </div>
                {detail.notes && <div className="sm:col-span-2"><strong>Notas:</strong> {detail.notes}</div>}
              </div>

              {detail.created_articles?.length > 0 && (
                <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-200">
                  ⚠️ Esta recepción creó {detail.created_articles.length} artículo(s) nuevo(s) en el catálogo (flag <code>external_origin</code>):
                  {' '}{detail.created_articles.map(a => a.name).join(', ')}.
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Medicamento</TableHead>
                    <TableHead>Lote</TableHead>
                    <TableHead>Vto.</TableHead>
                    <TableHead className="text-right">Recibido</TableHead>
                    <TableHead className="text-right">Disponible</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.items.map((it) => {
                    const remaining = it.quantity_remaining ?? '-';
                    const consumed = it.lot_active === false || remaining === 0;
                    return (
                      <TableRow key={it.lot_id}>
                        <TableCell>{it.article_name} <span className="text-xs text-muted-foreground">· {unitOf(it.article_id)}</span></TableCell>
                        <TableCell>{it.lot_number}</TableCell>
                        <TableCell>{it.expiration_date || '-'}</TableCell>
                        <TableCell className="text-right">{it.quantity}</TableCell>
                        <TableCell className="text-right">{remaining}</TableCell>
                        <TableCell>
                          {consumed ? (
                            <Badge variant="outline" className="bg-slate-100 text-slate-700">Consumido</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">En subdepósito (FEFO)</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            {detail && (
              <Button variant="outline" onClick={() => handlePrint(detail)} data-testid="rem-detail-print">
                <FileText className="w-4 h-4 mr-2" /> Imprimir formulario
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reporte mensual dialog */}
      <Dialog open={reportOpen} onOpenChange={(o) => { setReportOpen(o); if (!o) setReportData(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="rem-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-rose-600" /> Reporte mensual de Ingresos de Familiares (REM)</DialogTitle>
            <DialogDescription>Qué residente trajo medicación, mes a mes: fecha, quién recibió, residente y medicación.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end border-b pb-3">
            <div>
              <Label>Desde</Label>
              <Input type="date" value={reportFilters.start_date} onChange={e => setReportFilters({ ...reportFilters, start_date: e.target.value })} data-testid="report-start-date" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={reportFilters.end_date} onChange={e => setReportFilters({ ...reportFilters, end_date: e.target.value })} data-testid="report-end-date" />
            </div>
            <div>
              <Label>Área</Label>
              <select
                value={reportFilters.area_id}
                onChange={e => setReportFilters({ ...reportFilters, area_id: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">(Todas)</option>
                {areas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <Button className="w-full" onClick={loadReport} data-testid="report-generate">
                {reportLoading ? 'Generando…' : 'Generar'}
              </Button>
            </div>
          </div>

          {reportData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{reportData.total} recepción(es) en el período.</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={downloadReportPdf} data-testid="report-pdf"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
                  <Button variant="outline" size="sm" onClick={downloadReportExcel} data-testid="report-excel"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
                </div>
              </div>
              {reportData.groups.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">Sin recepciones en el período seleccionado.</div>
              ) : reportData.groups.map(g => (
                <div key={g.key} data-testid={`report-month-${g.key}`}>
                  <div className="font-semibold text-sm bg-rose-50 dark:bg-rose-950/30 border-l-4 border-rose-600 px-3 py-1.5 mb-1 rounded-sm">
                    {g.label} — {g.count} recepción(es) · {g.items_total} ítem(s)
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Residente</TableHead>
                        <TableHead>Recibió</TableHead>
                        <TableHead>Responsable</TableHead>
                        <TableHead>Medicación que trajo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {g.rows.map((r, idx) => (
                        <TableRow key={`${g.key}-${idx}`}>
                          <TableCell className="whitespace-nowrap text-xs">{r.date_fmt}</TableCell>
                          <TableCell className="font-medium">{r.resident_name}</TableCell>
                          <TableCell className="text-xs">{r.received_by_name}</TableCell>
                          <TableCell className="text-xs">{r.responsible}</TableCell>
                          <TableCell className="text-xs">
                            {r.medications.map((m, i) => <div key={i}>{m}</div>)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.45.2 · Barrera de similitud antes de crear un artículo nuevo */}
      <Dialog open={!!createBarrier} onOpenChange={(o) => !o && setCreateBarrier(null)}>
        <DialogContent className="max-w-md border-2 border-amber-400" data-testid="rem-create-barrier-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" /> Antes de crear «{createBarrier?.pendingName}»
            </DialogTitle>
            <DialogDescription>
              Confirmá que ninguno de estos ya es el mismo medicamento. Si lo es, elegilo — evitás duplicar el catálogo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 text-sm">
            {/* Match exacto */}
            {createBarrier?.exact && (
              <button
                type="button"
                className="w-full text-left rounded-md border-2 border-emerald-400 bg-emerald-50 px-3 py-2 hover:bg-emerald-100"
                data-testid="rem-barrier-exact"
                onClick={() => selectFromBarrier(createBarrier.exact)}
              >
                <span className="text-xs font-semibold text-emerald-700 block mb-0.5">Coincidencia exacta en el catálogo</span>
                <span className="font-medium">{createBarrier.exact.name}</span>
                {createBarrier.exact.matched_alias && <span className="text-xs text-muted-foreground ml-1">(antes «{createBarrier.exact.matched_alias}»)</span>}
                <span className="text-xs text-emerald-600 block mt-0.5">Clic para usar esta ficha</span>
              </button>
            )}

            {/* Similares */}
            {!createBarrier?.exact && createBarrier?.similar?.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 space-y-1">
                <p className="text-xs font-medium text-amber-800 mb-1">Medicamentos similares en el catálogo:</p>
                {createBarrier.similar.map(s => (
                  <button
                    key={s.article_id}
                    type="button"
                    className="w-full text-left rounded px-2 py-1 text-sm hover:bg-amber-100 flex items-center justify-between"
                    data-testid={`rem-barrier-similar-${s.article_id}`}
                    onClick={() => selectFromBarrier(s)}
                  >
                    <span>{s.name}</span>
                    <span className="text-xs text-muted-foreground">{s.unit || 'unidad'}</span>
                  </button>
                ))}
              </div>
            )}

            {!createBarrier?.exact && createBarrier?.similar?.length === 0 && (
              <p className="text-xs text-muted-foreground italic px-1">No se encontraron medicamentos similares en el catálogo.</p>
            )}

            {/* Checkbox de confirmación */}
            <label className="flex items-start gap-2 cursor-pointer rounded-md border border-slate-200 p-3 hover:bg-muted/40 mt-2">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={createBarrier?.barrierConfirmed || false}
                data-testid="rem-barrier-confirm-check"
                onChange={e => setCreateBarrier(b => b ? { ...b, barrierConfirmed: e.target.checked } : b)}
              />
              <span className="text-sm leading-snug">
                Revisé la lista y confirmo que <strong>ninguno</strong> de los anteriores es el medicamento que necesito registrar
              </span>
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateBarrier(null)} data-testid="rem-barrier-cancel">
              Cancelar
            </Button>
            <Button
              disabled={!createBarrier?.barrierConfirmed}
              onClick={confirmCreateFromBarrier}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="rem-barrier-confirm-btn"
            >
              Solicitar alta de «{createBarrier?.pendingName}»
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.45.0 · Aviso post-REM: ítems que quedaron en standby de homologación */}
      <Dialog open={!!pendingNotice} onOpenChange={(o) => !o && setPendingNotice(null)}>
        <DialogContent className="max-w-lg border-2 border-amber-400" data-testid="rem-pending-notice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <span className="text-2xl">⚠️</span> Ingresos en espera de homologación
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm text-slate-700 space-y-3">
                <div>
                  La REM <b>{pendingNotice?.reception_number}</b> incluye <b>{pendingNotice?.items?.length}</b> medicamento{pendingNotice?.items?.length === 1 ? '' : 's'} que todavía no están en el catálogo maestro.
                </div>
                <div className="rounded border border-amber-300 bg-amber-50 p-3">
                  <div className="font-semibold text-amber-900 mb-1">Importante</div>
                  <div>
                    Estos ítems <b>NO suman stock del área</b> hasta que Farmacia los <b>homologue</b> (o los <b>vincule</b> a un medicamento ya existente del catálogo) desde el Centro de Mantenimiento → Homologación &amp; Fusión.
                  </div>
                </div>
                <div className="font-medium">Ítems en standby:</div>
                <ul className="list-disc pl-6 space-y-0.5 max-h-40 overflow-auto">
                  {pendingNotice?.items?.map((p, idx) => (
                    <li key={idx}>
                      <b>{p.article_name}</b> — {p.quantity} un.
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-slate-500">
                  Avisá a Farmacia para destrabar el ingreso lo antes posible. Mientras tanto, la REM impresa queda igual como comprobante al familiar.
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setPendingNotice(null)} data-testid="rem-pending-notice-ack">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-rem-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Eliminar REM</DialogTitle>
            <DialogDescription>
              Vas a eliminar la recepción <b>{deleteTarget?.reception_number}</b> de «{deleteTarget?.patient_name}». Esta acción <b>revierte el stock</b> que la REM agregó al subdepósito y no se puede deshacer.
              <br /><br />
              Si algún ítem ya fue consumido o despachado, el sistema bloqueará el borrado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-rem">
              {deleting ? 'Eliminando…' : 'Eliminar y revertir stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default NursingReceptions;
